import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ShelfsActions } from "@states/shelfs/shelfs.action-types";
import { catchError, debounceTime, exhaustMap, of, share, switchMap, withLatestFrom } from "rxjs";
import { AppState } from '../app.state';
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ShelfsService } from "src/app/services/shelfs.service";
import { UtilsService } from "src/app/edge/utils.service";
import { LoggerService } from "src/app/services/logger.service";
import { ContextualLoggerService } from "src/app/services/contextual-logger.service";
import { SharedActions } from "@states/shared/shared.action-types";

@Injectable()
export class ShelfsEffect {
    private baseLogger = inject(LoggerService);
    private context = 'ShelfsEffect';
    private logger = new ContextualLoggerService(this.baseLogger, this.context);

    public startLoadingShelfs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.startLoadingShelfs),
            exhaustMap(() => [ShelfsActions.setIsLoading({ isLoading: true }), ShelfsActions.getShelfs()]),
        ),
    );

    public getShelfs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.getShelfs),
            withLatestFrom(this.store$.pipe(select(state => state.shelfsState))),
            switchMap(([, { page, pageSize, filters, isLastPage }]) => {
                if (!isLastPage) {
                    return this.shelfsService.getShelfs(page, pageSize, filters)
                        .pipe(
                            switchMap(res => {
                                return [
                                    ShelfsActions.getShelfsSuccess({ shelfs: res.data }),
                                    ShelfsActions.setIsLoading({ isLoading: false }),
                                ];
                            }),
                            catchError(response => {
                                const errorMessage = this.utilsService.errMessage(response);
                                this.logger.error(errorMessage);
                                return [
                                    ShelfsActions.setIsLoading({ isLoading: false }),
                                ];
                            }),
                        );
                } else {
                    return of(ShelfsActions.setIsLoading({ isLoading: false }));
                }
            },
            ),
            share(),
        ),
    );

    public startLoadingShelfDetailsById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.startLoadingShelfDetailsById),
            exhaustMap(({ id }) => [ShelfsActions.setIsLoadingDetails({ isLoading: true }), ShelfsActions.getShelfDetailsById({ id })]),
        ),
    );

    public getShelfDetailsById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.getShelfDetailsById),
            switchMap(({ id }) =>
                this.shelfsService.getDetailsById(id)
                    .pipe(
                        switchMap(res => {
                            return [
                                ShelfsActions.getShelfDetailsByIdSuccess({ selectedShelf: res }),
                                ShelfsActions.setIsLoadingDetails({ isLoading: true }),
                            ];
                        }),
                        catchError(response => {
                            const errorMessage = this.utilsService.errMessage(response);
                            this.logger.error(errorMessage);
                            return [
                                ShelfsActions.setIsLoadingDetails({ isLoading: true })
                            ];
                        }),
                    ),
            ),
            share(),
        ),
    );

    public createShelf$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.createShelf),
            exhaustMap(({ dto }) =>
                [
                    ShelfsActions.setIsSaving({ isSaving: true }),
                    ShelfsActions.createShelfServerCall({ dto }),
                ],
            ),
            share(),
        ),
    );

    public createShelfServerCall$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.createShelfServerCall),
            switchMap(({ dto }) =>
                this.shelfsService.createShelf(dto)
                    .pipe(
                        switchMap(res => {
                            return [
                                ShelfsActions.createShelfSuccess({ shelf: res }),
                                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.SHELF_GENERATED_SUCCESS') }),
                                ShelfsActions.setIsSaving({ isSaving: false }),
                                ShelfsActions.resetList(),
                                ShelfsActions.startLoadingShelfs(),
                            ];
                        }),
                        catchError(response => {
                            return [
                                this.catchError(response),
                                ShelfsActions.setIsSaving({ isSaving: false }),
                                ShelfsActions.createShelfFail(),
                            ];
                        }),
                    ),
            ),
            share(),
        ),
    );

    public editShelf$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.editShelf),
            exhaustMap(({ id, dto }) =>
                [
                    ShelfsActions.setIsSaving({ isSaving: true }),
                    ShelfsActions.editShelfServerCall({ id, dto }),
                ],
            ),
            share(),
        ),
    );

    public editShelfServerCall$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.editShelfServerCall),
            switchMap(({ id, dto }) =>
                this.shelfsService.edit(id, dto)
                    .pipe(
                        switchMap(res => {
                            return [
                                ShelfsActions.editShelfSuccess(),
                                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.SHELF_UPDATED_SUCCESS') }),
                                ShelfsActions.setIsSaving({ isSaving: false }),
                                ShelfsActions.resetList(),
                                ShelfsActions.startLoadingShelfs(),
                            ];
                        }),
                        catchError(response => {
                            return [
                                this.catchError(response),
                                ShelfsActions.setIsSaving({ isSaving: false }),
                                ShelfsActions.editShelfFail(),
                            ];
                        }),
                    ),
            ),
            share(),
        ),
    );

    public deleteShelf$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.deleteShelf),
            exhaustMap(({ id, }) =>
                [
                    ShelfsActions.setIsSaving({ isSaving: true }),
                    ShelfsActions.deleteShelfServerCall({ id }),
                ],
            ),
            share(),
        ),
    );

    public deleteShelfServerCall$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.deleteShelfServerCall),
            switchMap(({ id }) =>
                this.shelfsService.delete(id)
                    .pipe(
                        switchMap(res => {
                            return [
                                ShelfsActions.deleteShelfSuccess({ id: id }),
                                ShelfsActions.setIsSaving({ isSaving: false }),
                                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.SHELF_DELETED_SUCCESS') }),
                            ];
                        }),
                        catchError(response => {
                            return [
                                ShelfsActions.deleteShelfFail(),
                                ShelfsActions.setIsSaving({ isSaving: false }),
                                this.catchError(response),
                            ];
                        }),
                    ),
            ),
            share(),
        ),
    );

    public setFilter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShelfsActions.setFilter),
            debounceTime(400),
            exhaustMap(() => [ShelfsActions.resetList(), ShelfsActions.startLoadingShelfs()]),
        ),
    );

    private catchError(response) {
        const errorMessage = this.utilsService.errMessage(response);
        return SharedActions.showMessage({ error: errorMessage });
    }

    constructor(
        private actions$: Actions,
        private store$: Store<AppState>,
        private translateService: TranslateService,
        private shelfsService: ShelfsService,
        private utilsService: UtilsService,
    ) {
    }
}